import { AfterViewInit, Component, Inject, Injectable, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NxDialogService, NxModalRef } from '@aposin/ng-aquila/modal';
import { EmployeeService } from '@core/services/employee.service';
import { Employee } from '@shared/models/policies/detail/employee/employee';
import { EmployeeResponse } from '@shared/models/policies/detail/employee/employeeResponse';
import { ModalInterface } from "app/interface/modal";
import moment, { isMoment, Moment } from 'moment';
import { BehaviorSubject } from 'rxjs';
import { NX_DATE_LOCALE, NxDateAdapter, NxDatepickerIntl } from '@aposin/ng-aquila/datefield';
import { TranslateService } from '@ngx-translate/core';
import { LocaleService } from '@core/services/locale.service';

@Injectable()
export class MyIntl extends NxDatepickerIntl {
    constructor(private translateService: TranslateService) {
        super();
        this.switchToMonthViewLabel = this.translateService.instant('common.date.month-view');
        this.switchToMultiYearViewLabel = this.translateService.instant('common.date.date-view');
    }
}

@Component({
    selector: 'app-employee',
    templateUrl: './employee.component.html',
    styleUrls: ['./employee.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EmployeeComponent implements OnInit, AfterViewInit, ModalInterface {
  @ViewChild('Employee') modalTemplateRef: TemplateRef<any>;
  initiator: BehaviorSubject<boolean> = new BehaviorSubject(false);
  modalDialogRef: NxModalRef<any, any>;
  confirm: BehaviorSubject<SubmitEvent> = new BehaviorSubject(null);

  formEmployee: FormGroup;

  employee: Employee = new Employee();
  edit = false;
  successFormSubmit = false;

  private submitEvent: SubmitEvent = null;

  constructor(
    private dialogService: NxDialogService,
    private employeeService: EmployeeService,
    public dateAdapter: NxDateAdapter<Moment>,
    private translateService: TranslateService,
    private localeService: LocaleService,
    @Inject(NX_DATE_LOCALE) private nxDateLocale: string,
    @Inject(NxDatepickerIntl) private intl: NxDatepickerIntl,   
  ) {

    this.translateService.get('common.date.month-view').subscribe( x => {
        this.intl.switchToMonthViewLabel = x;
    })

    this.translateService.get('common.date.date-view').subscribe( x => {
        this.intl.switchToMultiYearViewLabel = x;
    })

    translateService.onLangChange.subscribe(x => {
        let lang = this.localeService.languages.find(y => y.code.toLowerCase() == x.lang);
        if (lang && lang.iso) {
            intl.switchToMonthViewLabel = translateService.instant('common.date.month-view');
            intl.switchToMultiYearViewLabel = translateService.instant('common.date.date-view');
            this.nxDateLocale = lang.iso;
        }
    });
  }

  ngOnInit(): void {
    this.formEmployee = new FormGroup({
      name: new FormControl(this.employee.name, Validators.required),
      surname: new FormControl(this.employee.surname, Validators.required),
      dateOfBirth: new FormControl(moment(this.employee.dateOfBirth), Validators.required),
      email: new FormControl(this.employee.email),
      canceledDate: new FormControl(
        (this.employee.canceledDate != undefined && this.employee.canceledDate.length > 0) ?
          moment(this.employee.canceledDate).toISOString() : ''
        ),
      department: new FormControl(this.employee.department),
    });

  }

  openModal(): void {
    this.modalDialogRef = this.dialogService.open(this.modalTemplateRef, { showCloseIcon: true, width: '512px' });
    this.modalDialogRef.beforeClosed().subscribe(() => this.reloadContent());
  }

  closeModal(): void {
    this.modalDialogRef.close();
  }

  submitEmployee($event: SubmitEvent): void {

    if (!this.formEmployee.valid) {
      return;
    }

    let employee = this.formEmployee.getRawValue() as Employee;
    employee.id = this.employee.id;
    employee.dateOfBirth = moment(employee.dateOfBirth).isValid() ?
      moment(employee.dateOfBirth).format('YYYY-MM-DD') : null;

    this.submitEvent = $event;
    if (this.edit) {
      employee.canceledDate = moment(employee.canceledDate).isValid() ?
        moment(employee.canceledDate).format('YYYY-MM-DD') : null;

      this.employeeService.updateEmployee(employee).subscribe((response: EmployeeResponse) => {
        this.processResponse(response)
      });

    } else {
      this.employeeService.insertEmployee(employee).subscribe((response: EmployeeResponse) => {
        this.processResponse(response)
      })
    }
  }

  private processResponse(response: EmployeeResponse): boolean {
    if (response.errors != undefined) {
      for (let k in response.errors) {
        this.formEmployee.get(k).setErrors({ valid: false });
      }
      return false;
    }

    if (response.id != undefined && !isNaN(response.id)) {
      this.successFormSubmit = true;
      return true;
    }

    return false;
  }


  reloadContent(){
    if(this.formEmployee.valid){
      this.confirm.next(this.submitEvent);
    }
  }

  ngAfterViewInit(): void {
    this.initiator.next(true);
  }

  inputData(inputData?: any): void {
    if (inputData != null) {
      this.employee = inputData as Employee;
    }

    if (Object.values(this.employee).find(x => x !== null)) {
      this.edit = true;
    }
  }
}
